import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Loader2, Star, Mail, Archive, ArchiveRestore, CheckCircle2, Circle, Inbox, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function StarRow({ value }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="inline-flex items-center gap-0.5 text-primary" title={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className="w-3.5 h-3.5"
          fill={n <= value ? "currentColor" : "none"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch { return iso; }
}

export default function Admin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minRating, setMinRating] = useState("any");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [patchingId, setPatchingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_archived: includeArchived, unread_only: unreadOnly };
      if (minRating !== "any") params.min_rating = Number(minRating);
      const [listR, statsR] = await Promise.all([
        api.get("/admin/feedback", { params }),
        api.get("/admin/feedback/stats"),
      ]);
      setItems(listR.data);
      setStats(statsR.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minRating, unreadOnly, includeArchived]);

  const patch = async (id, body) => {
    setPatchingId(id);
    try {
      const r = await api.patch(`/admin/feedback/${id}`, body);
      setItems((prev) => prev.map((x) => (x.id === id ? r.data : x)));
      // Refresh stats but don't block
      api.get("/admin/feedback/stats").then((s) => setStats(s.data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setPatchingId(null);
    }
  };

  const filteredCount = items.length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8 fade-in" data-testid="admin-page">
        <div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Admin</p>
          <h1 className="text-3xl tracking-tight" style={{ fontFamily: 'Manrope' }}>
            User Feedback
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Every message submitted through the "Help us improve" prompt lands here.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats?.total ?? "—" },
            { label: "Unread", value: stats?.unread ?? "—" },
            { label: "Archived", value: stats?.archived ?? "—" },
            { label: "Avg rating", value: stats?.average_rating ?? "—" },
          ].map((s) => (
            <Card key={s.label} className="p-4 rounded-2xl border-border/60 shadow-none">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</p>
              <p className="text-2xl mt-1 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                {s.value}
              </p>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="p-4 rounded-2xl border-border/60 shadow-none">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Rating</Label>
              <Select value={minRating} onValueChange={setMinRating}>
                <SelectTrigger className="w-[130px] h-9 rounded-full" data-testid="filter-rating">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">≥ 1 ★</SelectItem>
                  <SelectItem value="2">≥ 2 ★</SelectItem>
                  <SelectItem value="3">≥ 3 ★</SelectItem>
                  <SelectItem value="4">≥ 4 ★</SelectItem>
                  <SelectItem value="5">5 ★ only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={unreadOnly} onCheckedChange={setUnreadOnly} id="unread-only" data-testid="filter-unread" />
              <Label htmlFor="unread-only" className="text-sm">Unread only</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} id="include-archived" data-testid="filter-archived" />
              <Label htmlFor="include-archived" className="text-sm">Include archived</Label>
            </div>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={load} className="rounded-full" data-testid="refresh-button">
              <RefreshCw className="w-4 h-4 mr-1" strokeWidth={1.5} /> Refresh
            </Button>
          </div>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground p-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading feedback…
          </div>
        ) : filteredCount === 0 ? (
          <Card className="p-12 rounded-3xl border-border/60 shadow-none text-center">
            <Inbox className="w-8 h-8 mx-auto text-muted-foreground/60" strokeWidth={1.5} />
            <p className="mt-3 text-muted-foreground">No feedback matches these filters.</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="feedback-list">
            {items.map((it) => {
              const who = it.user_email || it.reply_email || "anonymous";
              const replyTo = it.reply_email || it.user_email;
              const subject = `Re: your feedback on ${window.location.host}`;
              return (
                <Card
                  key={it.id}
                  className={`p-5 rounded-2xl border-border/60 shadow-none ${
                    !it.is_read ? "border-l-4 border-l-primary" : ""
                  } ${it.is_archived ? "opacity-60" : ""}`}
                  data-testid="feedback-item"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium tracking-tight" style={{ fontFamily: 'Manrope' }}>
                          {it.user_name || who}
                        </span>
                        {it.user_email && it.user_name && (
                          <span className="text-xs text-muted-foreground">{it.user_email}</span>
                        )}
                        {!it.is_read && !it.is_archived && (
                          <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">new</Badge>
                        )}
                        {it.is_archived && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">archived</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(it.created_at)}
                        {it.page && <> · <span className="font-mono">{it.page}</span></>}
                      </p>
                    </div>
                    <StarRow value={it.rating} />
                  </div>

                  <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/85">
                    {it.message}
                  </p>

                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    {replyTo && (
                      <a
                        href={`mailto:${replyTo}?subject=${encodeURIComponent(subject)}`}
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        data-testid={`reply-${it.id}`}
                      >
                        <Mail className="w-4 h-4" strokeWidth={1.5} /> Reply to {replyTo}
                      </a>
                    )}
                    <div className="flex-1" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => patch(it.id, { is_read: !it.is_read })}
                      className="rounded-full"
                      disabled={patchingId === it.id}
                      data-testid={`toggle-read-${it.id}`}
                    >
                      {it.is_read ? (
                        <><Circle className="w-4 h-4 mr-1" strokeWidth={1.5} /> Mark unread</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Mark read</>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => patch(it.id, { is_archived: !it.is_archived })}
                      className="rounded-full"
                      disabled={patchingId === it.id}
                      data-testid={`toggle-archive-${it.id}`}
                    >
                      {it.is_archived ? (
                        <><ArchiveRestore className="w-4 h-4 mr-1" strokeWidth={1.5} /> Unarchive</>
                      ) : (
                        <><Archive className="w-4 h-4 mr-1" strokeWidth={1.5} /> Archive</>
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
